'use client';

import { type FC, type ReactNode, useMemo } from 'react';
import { cn } from '../utils/cn';
import { SkinImage } from './ui/SkinImage';
import { Button } from './ui/Button';
import { PrizeNamePriceRow } from './PrizeNamePriceRow';
import { RarityGlowOverlay } from './RarityGlowOverlay';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { rarityGradient } from '../utils/rarityGradient';

interface PrizeCardShellProps {
  /** Приз: имя разбирается внутри `PrizeNamePriceRow`, цвет редкости заливает карточку. */
  prize: {
    hashName: string;
    name: string;
    imageUrl?: string | null;
    rarityColor?: string | null;
  };
  /** Цена в монетах. */
  credits: number;
  /** Цена Steam. У `DrawCard` такого поля нет — тогда не передаётся. */
  steamPrice?: number | null;
  /** Подпись перед ценой на десктопе («за»). Есть не у всех карточек. */
  priceCaption?: ReactNode;
  /** ★ / StatTrak™ / Souvenir в строке оружия — сегодня только у `DrawCard`. */
  showQualityBadges?: boolean;

  /** Бейджи, таймер или заголовок сверху. */
  topSlot?: ReactNode;
  /** Где живёт верхний слот. `float` — поверх картинки (карточка сама позиционирует
   *  абсолютом, как `DailyGiveawayCard` и `RaffleCard`), `flow` — строкой над картинкой
   *  (`DrawCard`). От этого же зависит высота области картинки: там, где сверху стоит
   *  строка, картинке оставлено меньше. Это сегодняшнее расхождение, а не правило. */
  topSlotPlacement?: 'float' | 'flow';

  /** Блок статистики — у каждой карточки своё содержимое (места, билеты, участники). */
  statsSlot?: ReactNode;

  /** Содержимое белой кнопки внизу. Не передано — кнопки нет вовсе. */
  footerLabel?: ReactNode;
  onFooterClick?: (e: React.MouseEvent) => void;
  footerDisabled?: boolean;
  /** Плашка над кнопкой — сегодня только «шанс на победу» у `DrawCard`. */
  footerAboveSlot?: ReactNode;

  /** Нажатие по всей карточке (`DailyGiveawayCard`). */
  onCardClick?: () => void;
  /** Нажатие по картинке (`RaffleCard` — у него картинка отдельная кнопка). */
  onImageClick?: () => void;

  className?: string;
}

/**
 * Общий каркас карточки приза розыгрыша: заливка редкостью, засветка, картинка,
 * строка «оружие + название + цена» и белая кнопка внизу — плюс три слота под то,
 * что у карточек действительно разное: верхние бейджи, блок статистики и подпись кнопки.
 *
 * Сведено в #544 из `DailyGiveawayCard`, `DrawCard` и `RaffleCard`, где обе раскладки
 * были написаны заново в каждой карточке — шесть экземпляров на три карточки.
 * Развилка `useIsDesktop()` тоже жила трижды, теперь она здесь.
 */
export const PrizeCardShell: FC<PrizeCardShellProps> = ({
  prize,
  credits,
  steamPrice,
  priceCaption,
  showQualityBadges,
  topSlot,
  topSlotPlacement = 'float',
  statsSlot,
  footerLabel,
  onFooterClick,
  footerDisabled,
  footerAboveSlot,
  onCardClick,
  onImageClick,
  className = '',
}) => {
  const isDesktop = useIsDesktop();
  const cardStyle = useMemo(() => ({ background: rarityGradient(prize.rarityColor) }), [prize.rarityColor]);

  const image = (
    <SkinImage name={prize.name} src={prize.imageUrl} imgClassName="w-full h-full object-contain" />
  );

  const namePrice = (layout: 'mobile' | 'desktop', extra?: string) => (
    <PrizeNamePriceRow
      hashName={prize.hashName}
      fallbackName={prize.name}
      credits={credits}
      steamPrice={steamPrice}
      layout={layout}
      priceCaption={layout === 'desktop' ? priceCaption : undefined}
      showQualityBadges={showQualityBadges}
      className={extra}
    />
  );

  if (isDesktop) {
    return (
      <div
        className={cn(
          'group relative flex w-full flex-row overflow-hidden rounded-[21px] text-white',
          onCardClick && 'cursor-pointer',
          className,
        )}
        style={cardStyle}
        onClick={onCardClick}
      >
        <RarityGlowOverlay />
        <div className="relative z-[1] flex min-w-0 flex-1 flex-col gap-4 p-4">
          {topSlot}
          {namePrice('desktop')}
          {statsSlot}
          {footerLabel && (
            <div className="mt-auto">
              {footerAboveSlot}
              <Button
                type="button"
                size="m"
                stretched
                onClick={onFooterClick}
                disabled={footerDisabled}
                className="!bg-white font-semibold !text-black"
              >
                {footerLabel}
              </Button>
            </div>
          )}
        </div>

        <ImageArea onClick={onImageClick} className="relative flex w-[440px] shrink-0 items-center justify-center p-2">
          <div
            className="relative flex h-full w-full items-center justify-center transition-transform duration-300 group-hover:scale-105"
            style={{ filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.55))' }}
          >
            {image}
          </div>
        </ImageArea>
      </div>
    );
  }

  const floating = topSlotPlacement === 'float';

  return (
    <div
      className={cn(
        'relative flex w-full flex-col overflow-hidden rounded-[21px] p-0 text-white',
        onCardClick && 'cursor-pointer',
        className,
      )}
      style={cardStyle}
      onClick={onCardClick}
    >
      <RarityGlowOverlay />
      {floating && topSlot}

      <div className="relative flex w-full flex-col">
        {!floating && topSlot}

        <ImageArea
          onClick={onImageClick}
          className={cn(
            'flex items-center justify-center px-4',
            floating ? 'h-[220px] pt-4' : 'h-[210px]',
          )}
          style={{ filter: 'drop-shadow(0 12px 20px rgba(0,0,0,0.55))' }}
        >
          {image}
        </ImageArea>

        {namePrice('mobile', 'mt-1 px-4')}

        {statsSlot && <div className="px-4 pb-2.5 pt-2">{statsSlot}</div>}

        {footerLabel && (
          <div className="mt-auto px-4 pb-4">
            {footerAboveSlot}
            <Button
              type="button"
              size="l"
              stretched
              onClick={onFooterClick}
              disabled={footerDisabled}
              className="h-[45px] !bg-white font-semibold !text-black"
            >
              {footerLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Область картинки: кнопка, если по ней можно нажать, иначе просто блок.
 *  Вложенная настоящая `<button>` внутри кликабельной карточки была бы невалидным HTML,
 *  поэтому нажатие по картинке и нажатие по карточке — взаимоисключающие. */
const ImageArea: FC<{
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}> = ({ onClick, className, style, children }) => {
  if (!onClick) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} style={style}>
      {children}
    </button>
  );
};
